/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { environment } from '@environments/environment';
import {
  RoutesActivationModel,
  AppConfigModel,
  StepsConfigModel,
  EnvironmentModel,
  ThemeViews
} from '@models/.';
import { Inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  constructor(
    @Inject('APP_CONFIG') private appConfig: AppConfigModel,
    @Inject('STEPS_CONFIG') private stepsConfig: StepsConfigModel,
    @Inject('ROUTES_ACTIVATION')
    private routesActivation: RoutesActivationModel
  ) {}

  /**
   * restituisce configurazione app
   */
  getConfig(): AppConfigModel {
    return (
      (this.appConfig && { ...this.appConfig }) || {
        theme: {},
        generic: {},
        dev: {},
        security: {},
        wizard: {},
        survey: {},
        form: {},
        upload: {},
        publisher: {},
        labels: {},
        recognition: {},
        digitalSignature: {},
        obligations: {},
        documentsRequest: {},
        quotationCalculate: {},
        quotation: {},
        dashboard: {},
        analytics: {}
      }
    );
  }

  /**
   * restituisce copia dell'enviroment
   */
  getEnvironment(): EnvironmentModel {
    return { ...environment };
  }

  /**
   * restituisce configurazione step
   */
  getStepsConfig(): StepsConfigModel {
    return (this.stepsConfig && { ...this.stepsConfig }) || {};
  }

  /**
   * restituisce configurazione di attivazione delle rotte
   */
  getRoutesActivation(): RoutesActivationModel {
    return (this.routesActivation && { ...this.routesActivation }) || {};
  }

  /**
   * restituisce il nome del pacchetto con versione
   */
  getAppVersionAndName(target: 'container' | 'core' = 'container'): string {
    const version = this.getAppVersion(target);
    const name = this.getAppName(target);

    return `${name} v${version} `;
  }

  /**
   * restituisce il numero di versione dell'app
   */
  getAppVersion(target: 'container' | 'core' = 'container'): string {
    const versions = {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      container: window['APP_CONTAINER_VERSION'],
      // eslint-disable-next-line @typescript-eslint/dot-notation
      core: window['APP_CORE_VERSION']
    };

    return versions[target] || '';
  }

  /**
   * restituisce il nome del pacchetto
   */
  getAppName(target: 'container' | 'core' = 'container'): string {
    const names = {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      container: window['APP_CONTAINER_NAME'],
      // eslint-disable-next-line @typescript-eslint/dot-notation
      core: window['APP_CORE_NAME']
    };

    return names[target] || '';
  }

  /**
   * restituisce l'ambiente in cui è in esecuzione l'app
   */
  getHostEnv(): 'localhost' | 'develop' {
    const host = location.host;

    if (host.indexOf('localhost') > -1) {
      return 'localhost';
    } else if (host.indexOf('sviluppo')) {
      return 'develop';
    } else {
      return null;
    }
  }

  /**
   * definisce il locale
   */
  getLocale(): string {
    return this.getEnvironment().locale || 'it-IT';
  }

  /**
   * stabilisce se un driver di configurazione è attivo
   */
  isDriverEnabled(
    domainKey: string,
    driverKey: string,
    expectedValue?: any
  ): boolean {
    const value =
      this.getConfig()[domainKey] && this.getConfig()[domainKey][driverKey];

    if (typeof expectedValue === 'undefined') {
      return value === true;
    } else {
      return value === expectedValue;
    }
  }

  /**
   * stabilisce se la view è attiva in base alla configurazione
   */
  isViewActive(code: ThemeViews): boolean {
    const activeViews = this.getConfig().theme.activeViews || [];

    return !code || !!activeViews.find(x => x[0] === code && x[1] === true);
  }

  /**
   * definisce se l'app è standalone
   */
  isStandalone(): boolean {
    return this.getEnvironment().flowCode === 'standalone';
  }

  /**
   * definisce se l'app è a operazione
   */
  isOperation(): boolean {
    return this.getEnvironment().flowCode === 'operation';
  }

  /**
   * determina se è attiva la dev mode
   */
  isDevMode(): boolean {
    return !!this.getEnvironment().devMode;
  }

  /**
   * determina se la demo è attiva
   */
  isDemoActive(): boolean {
    return this.isDevMode() && !!localStorage.getItem('useDemo');
  }
}
